import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { format, isValid, parseISO } from 'date-fns';
import { NotificationService } from '../notification/notification.service';
import { DateParts, NotificationType } from '../../shared/models/misc';
import {
  CLOUDFLARE_TRACE_URL,
  countryCodeFromCloudflareTrace,
  countryCodeFromGeoJs,
  countryCodeFromTimeZone,
  GEOJS_COUNTRY_URL,
  readBrowserTimeZone,
} from './country-lookup';

@Injectable({
  providedIn: 'root',
})
export class MiscService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly notificationService = inject(NotificationService);
  private readonly http = inject(HttpClient);

  pageContextSignal: WritableSignal<string | null> = signal(null);
  pageNameSignal: WritableSignal<string | null> = signal(null);
  timedButtonParentIdsSignal: WritableSignal<string[]> = signal([]);

  inputClass = 'inline-flex items-center gap-x-1.5 rounded-full px-2 py-0.5 text-md font-medium';

  private history: string[] = [];
  private countryLookup$: Observable<string> | null = null;

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.history.push(event.urlAfterRedirects);
      }
    });
  }

  getNumericValue(value: number | string): number | null {
    return typeof value === 'number' ? value : Number(value) || null;
  }

  getInviteStatus(status: number): string {
    switch (status) {
      case 1: return 'Invited';
      case 2: return 'Applied';
      case 3: return 'Hired';
      case 4: return 'Post';
      case 5: return 'Posted';
      case 6: return 'Completed';
      case 7: return 'Paid';
      case 8: return 'Started';
      case 9: return 'Invite Declined';
      case 10: return 'Declined';
      case 11: return 'Invite Removed';
      case 12: return 'Bonus Paid';
      case 13: return 'Un-hired';
      default: return 'Invite';
    }
  }

  getStatusColour(status: number): string {
    let baseClass = this.inputClass;
    switch (status) {
      case 1:
        baseClass += ' pill pill-teal bg-teal-50 dark:bg-teal-900/30 dark:pill-teal-900/30 text-teal-700 ring-teal-600 ring-opacity-20';
        break;
      case 2:
        baseClass += ' pill pill-blue bg-blue-50 dark:bg-blue-900/30 dark:pill-blue-900/30 text-blue-700 ring-blue-600 ring-opacity-20';
        break;
      case 3:
        baseClass += ' pill bg-primary/10 dark:bg-primary/20 text-accent ring-primary/25';
        break;
      case 4:
        baseClass += ' pill pill-orange bg-orange-50 dark:bg-orange-900/30 dark:pill-orange-900/30 text-orange-700 ring-orange-600 ring-opacity-20';
        break;
      case 5:
      case 6:
      case 7:
        baseClass += ' pill pill-green bg-green-50 dark:bg-green-900/30 dark:pill-green-900/30 text-green-700 ring-green-600 ring-opacity-20';
        break;
      case 8:
        baseClass += ' pill pill-fuchsia bg-fuchsia-50 dark:bg-fuchsia-900/30 dark:pill-fuchsia-900/30 text-fuchsia-700 ring-fuchsia-600 ring-opacity-20';
        break;
      case 9:
        baseClass += ' pill pill-gray bg-gray-50 dark:bg-gray-900/30 dark:pill-gray-900/30 text-gray-700 ring-gray-600 ring-opacity-20';
        break;
      case 10:
        baseClass += ' pill pill-rose bg-rose-50 dark:bg-red-900/30 dark:pill-rose-900/30 text-rose-700 ring-rose-600 ring-opacity-20';
        break;
      case 11:
        baseClass += ' pill pill-amber bg-amber-50 dark:bg-amber-900/30 dark:pill-amber-900/30 text-amber-700 ring-amber-600 ring-opacity-20';
        break;
      case 12:
        baseClass += ' pill pill-emerald bg-emerald-50 dark:bg-emerald-900/30 dark:pill-emerald-900/30 text-emerald-700 ring-emerald-600 ring-opacity-20';
        break;
      case 13:
        baseClass += ' pill pill-red bg-red-50 dark:bg-red-900/30 dark:pill-red-900/30 text-red-700 ring-red-600 ring-opacity-20';
        break;
      default:
        baseClass += ' pill pill-dark bg-gray-50 dark:bg-gray-900/30 dark:pill-gray-900/30 text-gray-700 ring-gray-600 ring-opacity-20';
        break;
    }
    return baseClass;
  }

  getStatusDot(status: number): string {
    switch (status) {
      case 1: return 'fill-teal-500';
      case 2: return 'fill-blue-500';
      case 3: return 'fill-primary';
      case 4: return 'fill-orange-500';
      case 5:
      case 6:
      case 7: return 'fill-green-500';
      case 8: return 'fill-fuchsia-500';
      case 9: return 'fill-gray-500';
      case 10: return 'fill-rose-500';
      case 11: return 'fill-amber-500';
      case 13: return 'fill-red-500';
      default: return 'fill-white';
    }
  }

  getShortHometown(homeTownName: string): string {
    if (homeTownName.split(',').length > 2) {
      const addressArray = homeTownName.split(',');
      let shortAddress = addressArray.slice(-2).join(' ');
      if (shortAddress.length > 22) {
        shortAddress = shortAddress.substring(0, 21) + '...';
      }
      return shortAddress;
    }

    if (homeTownName.length > 22) {
      return homeTownName.substring(0, 21) + '...';
    }
    return homeTownName;
  }

  getName(cm: { userAccount: { firstName: string; lastName: string } }): string {
    return `${this.capitalizeFirstLetter(cm.userAccount.firstName)} ${this.capitalizeFirstLetter(cm.userAccount.lastName)}`;
  }

  capitalizeFirstLetter(str: string): string {
    if (!str) {
      return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  arraysEqual(a: unknown[], b: unknown[]): boolean {
    return a.length === b.length && a.every((val, index) => val === b[index]);
  }

  updateQueryParams(key: string, value: string | number | null | undefined): void {
    const queryParams: Record<string, string | number | null> = {};
    queryParams[key] = value === null || value === undefined || value === '' ? null : value;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  getHistory(): string[] {
    return this.history;
  }

  getPreviousUrl(): string | null {
    return this.history.length > 1 ? this.history[this.history.length - 2] : null;
  }

  copyToClipboard(copyItem: string, copiedTitle?: string): void {
    if (!copyItem) {
      return;
    }

    navigator.clipboard.writeText(copyItem).then(() => {
      if (copiedTitle) {
        this.notificationService.sendMessage({
          message: `${copiedTitle} copied to clipboard!`,
          type: NotificationType.success,
        });
      }
    }).catch((err) => {
      console.error('Failed to copy:', err);
      if (copiedTitle) {
        this.notificationService.sendMessage({
          message: `Failed to copy ${copiedTitle}. Please try again`,
          type: NotificationType.error,
        });
      }
    });
  }

  formatShortNumber(value: number): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '0';
    }
    if (value < 1000) {
      return value.toString();
    }

    const units = [
      { v: 1e3, s: 'K' },
      { v: 1e6, s: 'M' },
      { v: 1e9, s: 'B' },
      { v: 1e12, s: 'T' },
      { v: 1e15, s: 'P' },
      { v: 1e18, s: 'E' },
    ];

    let index = units.length - 1;
    while (index > 0 && value < units[index].v) {
      index--;
    }

    const shortValue = (value / units[index].v)
      .toFixed(2)
      .replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1');
    return shortValue + units[index].s;
  }

  htmlToPlainText(html: string): string {
    const normalised = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|h[1-6]|li)>/gi, '$&\n')
      .replace(/<\/(ul|ol)>/gi, '\n');

    const el = document.createElement('div');
    el.innerHTML = normalised;
    const text = (el.textContent || '').replace(/\u00A0/g, ' ').trim();
    return text.replace(/\n{3,}/g, '\n\n');
  }

  formatTitle(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  getCountry(): Observable<string> {
    if (!this.countryLookup$) {
      this.countryLookup$ = this.lookupCountry().pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.countryLookup$;
  }

  private lookupCountry(): Observable<string> {
    return this.http.get<{ country?: string }>(GEOJS_COUNTRY_URL).pipe(
      map((response) => countryCodeFromGeoJs(response)),
      switchMap((code) => (code ? of(code) : this.lookupCountryFromCloudflare())),
      catchError(() => this.lookupCountryFromCloudflare()),
    );
  }

  private lookupCountryFromCloudflare(): Observable<string> {
    return this.http.get(CLOUDFLARE_TRACE_URL, { responseType: 'text' }).pipe(
      map((body) => countryCodeFromCloudflareTrace(body) || this.countryFromTimeZone()),
      catchError(() => of(this.countryFromTimeZone())),
    );
  }

  private countryFromTimeZone(): string {
    return countryCodeFromTimeZone(readBrowserTimeZone());
  }

  getCurrencySymbol(currencyCode: string): string {
    switch (currencyCode) {
      case 'GBP':
        return '£';
      case 'USD':
      default:
        return '$';
    }
  }

  toDateParts(input: Date | string): DateParts {
    const d = input instanceof Date ? input : parseISO(input);
    if (!isValid(d)) {
      throw new Error('toDateParts: Invalid date input');
    }

    return {
      day: Number(format(d, 'd')),
      month: format(d, 'MMMM'),
      year: Number(format(d, 'yyyy')),
    };
  }

  extractAgeRange(value: string): { min: number | null; max: number | null } {
    if (!value) {
      return { min: null, max: null };
    }
    if (/^\d+_\d+$/.test(value)) {
      const [min, max] = value.split('_').map(Number);
      return { min, max };
    }
    if (value.startsWith('over_')) {
      return { min: Number(value.replace('over_', '')), max: null };
    }
    if (value.startsWith('strictly_')) {
      return { min: Number(value.replace('strictly_', '')), max: null };
    }
    return { min: null, max: null };
  }

  formatTimeLeft(
    expiresAt: string | Date | null | undefined,
    nowEpochMs: number = Date.now(),
  ): string {
    if (!expiresAt) {
      return 'N/A';
    }

    const exp = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
    const expMs = exp.getTime();
    if (!Number.isFinite(expMs)) {
      return 'N/A';
    }

    const diffMs = expMs - nowEpochMs;
    if (diffMs <= 0) {
      return 'Expired';
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  getExpiryTimeLeftPercent(
    createdAt: string | Date | null | undefined,
    expiresAt: string | Date | null | undefined,
    nowEpochMs: number = Date.now(),
  ): number | null {
    if (!createdAt || !expiresAt) {
      return null;
    }

    const start = createdAt instanceof Date ? createdAt : new Date(createdAt);
    const end = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
    const startMs = start.getTime();
    const endMs = end.getTime();

    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return null;
    }

    const total = endMs - startMs;
    if (total <= 0) {
      return 0;
    }

    const diffLeft = endMs - nowEpochMs;
    const raw = (diffLeft / total) * 100;
    return Math.max(0, Math.min(100, raw));
  }
}
